import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i2$1 from '@angular/router';
import * as i1 from 'angular-oauth2-oidc';
import { Subject, Observable, of } from 'rxjs';
import * as i2 from '@auth0/angular-jwt';
import * as i1$1 from '@caloptima/portal-foundation';
import { HttpResponseHelper } from '@caloptima/portal-foundation';
import * as i1$2 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { publishReplay, refCount, catchError, switchMap, map } from 'rxjs/operators';
let AuthHelper = /*#__PURE__*/(() => {
  class AuthHelper {
    constructor(authService, jwtHelper) {
      this.authService = authService;
      this.jwtHelper = jwtHelper;
      this.tokenExpiringSubject = new Subject();
      this.scripts = [];
      this.tokenExpirationTimer = 0;
      this.tokenExpirationWarningTime = 0;
    }
    setExpirationTimeout(expiresIn) {
      let expiration = (expiresIn - this.tokenExpirationWarningTime) * 1000;
      if (this.tokenExpirationTimer != 0) {
        window.clearTimeout(this.tokenExpirationTimer);
      }
      this.tokenExpirationTimer = window.setTimeout(() => {
        this.tokenExpiringSubject.next(null);
        window.clearTimeout(this.tokenExpirationTimer);
        this.tokenExpirationTimer = 0;
        this.tokenExpirationTime = 0;
      }, expiration);
    }
    setIsLoggingIn(value) {
      this._isLoggingIn = value;
    }
    isLoggingIn() {
      return this._isLoggingIn;
    }
    tokenNotExpired() {
      let token = this.authService.getAccessToken();
      if (token == null) {
        return false;
      } else {
        return !this.jwtHelper.isTokenExpired(token);
      }
    }
    oauthLogin(response) {
      let subject = new Subject();
      var hash = "#access_token=" + response.access_token + "&refresh_token=" + response.refresh_token + "&id_token=" + response.id_token + "&token_type=" + response.token_type + "&expires_in=" + response.expires_in;
      this.authService.requestAccessToken = true;
      if (response.nonce != null) {
        hash = hash + "&state=" + response.nonce;
      }
      this.authService.tryLogin({
        customHashFragment: hash
      }).then(() => {
        this._isLoggingIn = false;
        // If the tokenExpiring subscriber has been called before attempting to logon,
        // then set the timeout now
        if (this.tokenExpirationWarningTime != 0) {
          this.setExpirationTimeout(response.expires_in);
        } else {
          this.tokenExpirationTime = response.expires_in;
        }
        subject.next(null);
      });
      return subject.asObservable();
    }
    tokenExpiring(warningTime) {
      this.tokenExpirationWarningTime = warningTime;
      if (this.tokenExpirationTime == 0) {
        this.setExpirationTimeout(this.tokenExpirationTime);
      }
      return this.tokenExpiringSubject.asObservable();
    }
    loadScript(script) {
      return new Observable(observer => {
        const existingScript = this.scripts.find(s => s.name === script.name);
        // Complete if already loaded
        if (existingScript && existingScript.loaded) {
          observer.next(existingScript);
          observer.complete();
        } else {
          // Add the script
          this.scripts = [...this.scripts, script];
          // Load the script
          const scriptElement = document.createElement('script');
          scriptElement.type = 'text/javascript';
          scriptElement.src = script.src;
          scriptElement.onload = () => {
            script.loaded = true;
            observer.next(script);
            observer.complete();
          };
          scriptElement.onerror = error => {
            observer.error('Couldn\'t load script ' + script.src);
          };
          document.getElementsByTagName('body')[0].appendChild(scriptElement);
        }
      });
    }
    static {
      this.ɵfac = function AuthHelper_Factory(t) {
        return new (t || AuthHelper)(i0.ɵɵinject(i1.OAuthService), i0.ɵɵinject(i2.JwtHelperService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthHelper,
        factory: AuthHelper.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AuthHelper;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AuthenticationConfig = /*#__PURE__*/(() => {
  class AuthenticationConfig {
    constructor(config) {
      this.config = config;
      config.subscribe(() => {
        this.baseServiceUrl = this.config.getConfig('BaseAuthenticationServiceUrl');
      });
      this.AuthorizeUserComponent = config.getConfig('AuthorizeUserComponent');
      this.SessionStateKey = config.getConfig('SessionStateKey');
    }
    get BaseAuthenticationServiceUrl() {
      if (this.baseServiceUrl == null) this.baseServiceUrl = this.config.getConfig('BaseAuthenticationServiceUrl');
      return this.baseServiceUrl;
    }
    static {
      this.ɵfac = function AuthenticationConfig_Factory(t) {
        return new (t || AuthenticationConfig)(i0.ɵɵinject(i1$1.AppConfig));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthenticationConfig,
        factory: AuthenticationConfig.ɵfac
      });
    }
  }
  return AuthenticationConfig;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
** Service extends the CanActivate interface allowing the service to be
** used as a guard in the routes. Default behavior is to check for a valid
** token if not found redirect to authorize component.
*/
let AuthGuard = /*#__PURE__*/(() => {
  class AuthGuard {
    // jwtHelper: JwtHelper = new JwtHelper();
    // Constructor used to inject OAuth handler service and Router
    constructor(authService, router, config, authHelper) {
      this.authService = authService;
      this.router = router;
      this.config = config;
      this.authHelper = authHelper;
    }
    canActivate(next, state) {
      return this.checkLogin(state.url);
    }
    canActivateChild(route, state) {
      return this.canActivate(route, state);
    }
    checkLogin(url) {
      if (this.authService.hasValidAccessToken() && this.authHelper.tokenNotExpired()) {
        return true;
      } else if (this.authService.hasValidAccessToken() && !this.authHelper.tokenNotExpired()) {
        this.authService.logOut(true);
        sessionStorage.removeItem(this.config.SessionStateKey);
      } else if (!this.authHelper.isLoggingIn() && this.config.AuthorizeUserComponent) {
        this.authHelper.setIsLoggingIn(true);
        localStorage.setItem('redirectUrl', url);
        this.router.navigate([this.config.AuthorizeUserComponent]);
      }
      return false;
    }
    static {
      this.ɵfac = function AuthGuard_Factory(t) {
        return new (t || AuthGuard)(i0.ɵɵinject(i1.OAuthService), i0.ɵɵinject(i2$1.Router), i0.ɵɵinject(AuthenticationConfig), i0.ɵɵinject(AuthHelper));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthGuard,
        factory: AuthGuard.ɵfac
      });
    }
  }
  return AuthGuard;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var AccountStatus = /*#__PURE__*/function (AccountStatus) {
  AccountStatus[AccountStatus["Pending"] = 0] = "Pending";
  AccountStatus[AccountStatus["Submitted"] = 1] = "Submitted";
  AccountStatus[AccountStatus["PasswordExpired"] = 2] = "PasswordExpired";
  AccountStatus[AccountStatus["PasswordSetupNeeded"] = 3] = "PasswordSetupNeeded";
  AccountStatus[AccountStatus["Active"] = 4] = "Active";
  AccountStatus[AccountStatus["Inactive"] = 5] = "Inactive";
  AccountStatus[AccountStatus["Locked"] = 6] = "Locked";
  AccountStatus[AccountStatus["AdminLocked"] = 7] = "AdminLocked";
  AccountStatus[AccountStatus["Deactivated"] = 8] = "Deactivated";
  return AccountStatus;
}(AccountStatus || {});
class AuthenticationFactor {}
var AuthenticationServiceStatus = /*#__PURE__*/function (AuthenticationServiceStatus) {
  AuthenticationServiceStatus[AuthenticationServiceStatus["NoError"] = 0] = "NoError";
  AuthenticationServiceStatus[AuthenticationServiceStatus["OnetimePasscodeRequired"] = 1] = "OnetimePasscodeRequired";
  AuthenticationServiceStatus[AuthenticationServiceStatus["NotallFactorsAuthenticated"] = 2] = "NotallFactorsAuthenticated";
  return AuthenticationServiceStatus;
}(AuthenticationServiceStatus || {});
class AuthenticationServiceResponse {}
class FindMemberRequest {}
class FindMemberResponse {}
class FindUsersRequest {}
class GetTokenRequest {}
class GetTokenResponse {}
class KnowledgeBase {}
class LoginRequest {}
class LoginResponse {}
class MemberProfileRequest {}
class MemberProfile {}
class UserAddress {}
class UserProfile {}
class AuthProfile {}
class FindMemberUserRequest {}
class ForgotUsernameRequest {}
class ForgotUsernameResponse {}
var UserNameStatus = /*#__PURE__*/function (UserNameStatus) {
  UserNameStatus[UserNameStatus["Available"] = 0] = "Available";
  UserNameStatus[UserNameStatus["Unavailable"] = 1] = "Unavailable";
  UserNameStatus[UserNameStatus["AlreadyRegistered"] = 2] = "AlreadyRegistered";
  return UserNameStatus;
}(UserNameStatus || {});
class ValidateUserResponse {}
class RefreshTokenRequest {}
class DeleteRegistration {}
const httpOptions$1 = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  }),
  withCredentials: true
};
let AuthenticationService = /*#__PURE__*/(() => {
  class AuthenticationService {
    constructor(http, configuration, authHelper) {
      this.http = http;
      this.configuration = configuration;
      this.authHelper = authHelper;
    }
    checkUrls() {
      if (this.authenticateUserUrl == null) {
        let baseUrl = this.configuration.BaseAuthenticationServiceUrl;
        this.authenticateUserUrl = baseUrl + "login";
        this.getFactorsUrl = baseUrl + "factors";
        this.authenticateFactorUrl = baseUrl + "authenticateFactors";
        this.getTokenUrl = baseUrl + "getToken";
        this.registerUrl = baseUrl + "register";
        this.updateUserUrl = baseUrl + "update";
        this.updateSelectedUserUrl = baseUrl + "updateSelectedUser";
        this.updateSelectedUserProfileUrl = baseUrl + "updateSelectedUserProfile";
        this.validateUserUrl = baseUrl + "validateUser";
        this.findMemberUrl = baseUrl + "findMember";
        this.findUsersUrl = baseUrl + "findUsers";
        this.findUserStatusResetPassword = baseUrl + "FindUserAndResetLock";
        this.refreshCodeUrl = baseUrl + "refreshCode";
        this.resetPasswordUrl = baseUrl + "resetPassword";
        this.changePasswordUrl = baseUrl + "changePassword";
        this.forgotUsernameUrl = baseUrl + "forgotUsername";
        this.findMemberUserUrl = baseUrl + "findMemberUser";
        this.getDfpJavascriptUrl = baseUrl + "getDfpJavascriptUrl";
        this.deletePendingRegistrationUrl = baseUrl + "deletePendingRegistration";
      }
    }
    loginUser(userName, password, clientId, rememberMe) {
      this.checkUrls();
      try {
        var x = this;
        var request = new LoginRequest();
        request.userName = userName;
        request.password = password;
        request.clientId = clientId;
        if (rememberMe) {
          return this.getFingerPrint(clientId).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)), switchMap(fingerprintResult => {
            request.fingerprint = fingerprintResult;
            return this.http.post(this.authenticateUserUrl, request, httpOptions$1);
          }));
        } else {
          return this.http.post(this.authenticateUserUrl, request, httpOptions$1).pipe(catchError(error => {
            return HttpResponseHelper.handleError(error);
          }));
        }
      } catch (ex) {
        // log error
        return null;
      }
    }
    getFactors() {
      this.checkUrls();
      try {
        return this.http.get(this.getFactorsUrl, {
          withCredentials: true
        }).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    authenticateFactors(factors) {
      this.checkUrls();
      try {
        return this.http.post(this.authenticateFactorUrl, factors, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    getToken(clientId, redirectUri, scopes, nonce, rememberMe) {
      this.checkUrls();
      try {
        this.nonce = nonce;
        var request = new GetTokenRequest();
        request.clientID = clientId;
        request.redirectUri = redirectUri;
        request.scopes = scopes;
        request.nonce = nonce;
        request.remember = rememberMe;
        return this.http.post(this.getTokenUrl, request, httpOptions$1).pipe(publishReplay(1), refCount(), map(response => {
          this.refreshToken = response.refresh_token;
          response.nonce = nonce;
          return response;
        }), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    getRefreshToken(clientId) {
      this.checkUrls();
      try {
        var request = new RefreshTokenRequest();
        request.clientId = clientId;
        request.refreshToken = this.refreshToken;
        request.nonce = this.nonce;
        return this.http.post(this.refreshCodeUrl, request, httpOptions$1).pipe(map(response => {
          this.refreshToken = response.refresh_token;
          response.nonce = this.nonce;
          return response;
        }), publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    register(profile) {
      this.checkUrls();
      try {
        return this.http.post(this.registerUrl, profile, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    //update logged-in user profile
    updateUser(profile) {
      this.checkUrls();
      try {
        return this.http.post(this.updateUserUrl, profile, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    //update selected user (setup password)
    updateSelectedUser(profile) {
      this.checkUrls();
      try {
        return this.http.post(this.updateSelectedUserUrl, profile, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    //update user profile securely with login user
    updateSelectedUserProfile(profile) {
      this.checkUrls();
      try {
        return this.http.post(this.updateSelectedUserProfileUrl, profile, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    validateUser(userName, memberId) {
      this.checkUrls();
      try {
        var url = this.validateUserUrl + "/" + userName;
        if (memberId != null && memberId != "") url = url + "/" + memberId;
        return this.http.get(url, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    findMember(memberId, birthDate) {
      this.checkUrls();
      try {
        var request = new FindMemberRequest();
        request.memberID = memberId;
        request.birthDate = birthDate;
        return this.http.post(this.findMemberUrl, request, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    findUsers(lastName, email, clientId, pageNumber, pageSize) {
      this.checkUrls();
      try {
        var request = new FindUsersRequest();
        request.lastName = lastName;
        request.email = email;
        request.pageNumber = pageNumber;
        request.pageSize = pageSize;
        request.clientId = clientId;
        return this.http.post(this.findUsersUrl, request, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    //delete pending registrations before allowing to create new registration
    deletePendingRegistration(clientId, email) {
      this.checkUrls();
      try {
        var request = new DeleteRegistration();
        request.memeberEmailId = email;
        request.clientId = clientId;
        return this.http.post(this.deletePendingRegistrationUrl, request, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    findUserAndResetLock(lastName, email, clientId, pageNumber, pageSize) {
      this.checkUrls();
      try {
        var request = new FindUsersRequest();
        request.lastName = lastName;
        request.email = email;
        request.pageNumber = pageNumber;
        request.pageSize = pageSize;
        request.clientId = clientId;
        return this.http.post(this.findUserStatusResetPassword, request, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    findMemberUser(findMemberUserRequest) {
      this.checkUrls();
      try {
        return this.http.post(this.findMemberUserUrl, findMemberUserRequest, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    resetPassword(password, clientId) {
      this.checkUrls();
      try {
        return this.http.post(this.resetPasswordUrl, {
          password: password,
          clientId: clientId
        }, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    changePassword(password, clientId) {
      this.checkUrls();
      try {
        return this.http.post(this.changePasswordUrl, {
          password: password,
          clientId: clientId
        }, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    forgotUsername(clientId) {
      this.checkUrls();
      try {
        return this.http.post(this.forgotUsernameUrl, {
          clientId: clientId
        }, httpOptions$1).pipe(publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    dfpJavascriptUrl(clientId) {
      this.checkUrls();
      try {
        return this.http.post(this.getDfpJavascriptUrl, {
          clientId: clientId
        }, httpOptions$1).pipe(map(res => res.src), publishReplay(1), refCount(), catchError(error => HttpResponseHelper.handleError(error)));
      } catch (ex) {
        // log error
        return null;
      }
    }
    getFingerPrint(clientId) {
      return new Observable(observer => {
        let fingerprint;
        let sub = this.dfpJavascriptUrl(clientId).pipe(switchMap(scriptUrlResult => {
          let fingerprintScript = {
            name: 'fingerprint',
            src: scriptUrlResult,
            loaded: false
          };
          return this.authHelper.loadScript(fingerprintScript);
        })).subscribe(data => {
          if (data.loaded) {
            fingerprint = secureAuth.fingerprint.getAllResults(); //for string - use JSON.stringify(fingerprint);
          }
          sub.unsubscribe();
          observer.next(fingerprint);
          observer.complete();
        }, err => observer.error(err));
      });
    }
    static {
      this.ɵfac = function AuthenticationService_Factory(t) {
        return new (t || AuthenticationService)(i0.ɵɵinject(i1$2.HttpClient), i0.ɵɵinject(AuthenticationConfig), i0.ɵɵinject(AuthHelper));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthenticationService,
        factory: AuthenticationService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AuthenticationService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  }),
  withCredentials: true
};
let MockAuthenticationService = /*#__PURE__*/(() => {
  class MockAuthenticationService {
    constructor() {
      this.factors = [];
    }
    findUser(filterFunc) {
      let user = null;
      if (this.users != null) {
        let found = this.users.filter(filterFunc);
        if (found.length == 1) user = found[0];
      }
      return user;
    }
    loginUser(userName, password, clientId) {
      let user = this.findUser(x => x.userID === userName);
      if (user == null) throw new Error("No user profile is defined yet");
      if (password === user.password) {
        let response = new LoginResponse();
        response.requiresTwoFactor = true;
        return of(response);
      }
      throw new Error("User or password incorrect");
    }
    getFactors() {
      return of(this.factors);
    }
    setFactors(factors) {
      this.factors = factors;
    }
    authenticateFactors(factors) {
      let response = new AuthenticationServiceResponse();
      if (this.factors == null) response.status = AuthenticationServiceStatus.NoError;else if (factors == null || factors.length != this.factors.length) response.status = AuthenticationServiceStatus.NotallFactorsAuthenticated;else if (this.factors.length == 1) response.status = AuthenticationServiceStatus.OnetimePasscodeRequired;else response.status = AuthenticationServiceStatus.NoError;
      return of(response);
    }
    getToken(clientId, redirectUri, scopes, nonce, rememberMe) {
      let response = new GetTokenResponse();
      response.access_token = "eyJhbGciOiJSUzI1NiIsIng1dCI6IjlacThOdm1TdlJzUjlYdWRyYlZTc09weDN3VSIsImtpZCI6IjlacThOdm1TdlJzUjlYdWRyYlZTc09weDN3VSIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI0ZmE2NzAyODBmZDU0ZGQxYjIxYTU5ZmE3ZTk5MDMzMCIsInNjb3BlIjpbImVtYWlsIiwiZ2FsbGVyeW1hbmFnZW1lbnQiLCJvcGVuaWQiLCJwcm9maWxlIl0sImNsaWVudF9pZCI6IjkzMzUyODExYzQyNDQ3MDVhNmQ4OGRkOGY1MThmMGZjIiwic3ViIjoiZ2FyeS53aWxsaWFtc0BjYWxvcHRpbWEub3JnIiwiYXV0aF90aW1lIjoxNTU1NzEyMjcxLCJpZHAiOiJTZWN1cmVBdXRoMTIiLCJhbXIiOiJwYXNzd29yZCIsImlzcyI6Imh0dHBzOi8vbWVtYmVyLmNhbG9wdGltYS5vcmciLCJhdWQiOiJodHRwczovL21lbWJlci5jYWxvcHRpbWEub3JnIiwiZXhwIjoxNTU1NzE1ODcyLCJuYmYiOjE1NTU3MTIyNzJ9.NEsT_C8YP3DVEFqQ891e87xQfIokcSy1_7R9MHJ0hX0W8m1-uZebx6pvtLwPUaDX2sFjX-4X0JAQ9XmRuWkB8IBIzhx89UeBDBJvb1uHKIlO7s9VL-s3mjWEL8S3gbQEAdHp2YSwLn4LypC__gZiGF0ar7-_zI5Moa-WuYHY6TiUs2dsJbOXChogAf3MrSGKZIT7YHKHtj-ZmYX7NYQydwKKe5i8bydcsfv2eZdiIyC6pRAbhU2EE8V2ryEQERtTAdLT8aldUHY9YnyFJDcYgXULhkIuhLGwmGALxqBcF6CLgkCTvV9BqddKRwPn9xfqW7g5TA_EHtj7DyMox-nGGQ";
      response.id_token = "eyJhbGciOiJSUzI1NiIsIng1dCI6IjlacThOdm1TdlJzUjlYdWRyYlZTc09weDN3VSIsImtpZCI6IjlacThOdm1TdlJzUjlYdWRyYlZTc09weDN3VSIsInR5cCI6IkpXVCJ9.eyJpZHAiOiJTZWN1cmVBdXRoMTIiLCJhdXRoX3RpbWUiOjE1NTU3MTIyNzEsImFtciI6InBhc3N3b3JkIiwic3ViIjoiZ2FyeS53aWxsaWFtc0BjYWxvcHRpbWEub3JnIiwiZmFtaWx5X25hbWUiOiJXaWxsaWFtcyIsImdpdmVuX25hbWUiOiJHYXJ5IiwiZW1haWwiOiJnYXJ5LndpbGxpYW1zQGNhbG9wdGltYS5vcmciLCJlbWFpbF92ZXJpZmllZCI6InRydWUiLCJqdGkiOiI4MjJlYjJkOGRmMjU0MDNhOWQ0NGQ2MzczMTZmMGU4MCIsImF0X2hhc2giOiJuWWlON1otT09HcnBzU2g5LTFoMExBIiwiaWF0IjoxNTU1NzEyMjcyLCJpc3MiOiJodHRwczovL21lbWJlci5jYWxvcHRpbWEub3JnIiwiYXVkIjoiOTMzNTI4MTFjNDI0NDcwNWE2ZDg4ZGQ4ZjUxOGYwZmMiLCJleHAiOjE1NTU3MTU4NzIsIm5iZiI6MTU1NTcxMjI3Miwibm9uY2UiOiJBa3BxSWR3MGdVWTg1VzJOMW91T2RqVHdRb2dYSE5KZHptakFReWd5In0.2bnixvpIEN4R0XtMqWN1Lgy2E5mUzPbjZSkjcbp_Lt3gM6y9XKy0-cHb8e7HMa_t5M2Zj0DbAuoart8oK30wxG72UEkLsBATdqzT6FgUSqK5vGSYi3rtzsNVBCZmokmVwy2GlyBNHN-yBhdRFBk6rgaQAfcJ4e9pf2A0V5YCHbzdMwH-l9gygoE3A8JRGPvgRFJ7zYmlrw0HxVUhK_dbbbtq5UO89Hrwx5LVtq317lZc1vVJT_SX2sCzJwRrLvPQP4p_eIc4Tf0PsfbC4WwXPEuIFWmr-vA56DkYYIys6OsWTgs16O0fLXJoExPgeplX4X9UtunbTrLlDUzah8Osvw";
      response.expires_in = 3600;
      response.token_type = "Bearer";
      return of(response);
    }
    register(authProfile) {
      if (this.users == null) this.users = [];
      let profile = authProfile.userProfile;
      if (profile.mobilePhone != null) {
        this.factors.push({
          factorType: 'Call',
          factorID: 'PH1',
          value: profile.mobilePhone
        });
        this.factors.push({
          factorType: 'SMS',
          factorID: 'SMS1',
          value: profile.mobilePhone
        });
      }
      if (profile.email != null) {
        this.factors.push({
          factorType: 'Email',
          factorID: 'EM1',
          value: profile.email
        });
      }
      if (profile.knowledgeBase != null) {
        let id = 1;
        profile.knowledgeBase.forEach(kb => {
          this.factors.push({
            factorType: 'Question',
            factorID: 'KBQ' + id,
            value: kb.question
          });
          id++;
        });
      }
      let user = this.findUser(x => x.userID === profile.userID);
      if (user == null) this.users.push(profile);
      return of(null);
    }
    updateUser(authProfile) {
      let profile = authProfile.userProfile;
      let address = authProfile.address;
      let user = this.findUser(x => x.userID === profile.userID);
      if (user != null) {
        user.password = profile.password;
        user.firstName = profile.firstName;
        user.lastName = profile.lastName;
        user.mobilePhone = profile.mobilePhone;
        user.fax = profile.fax;
        user.email = profile.email;
        user.pin = profile.pin;
        user.birthDate = profile.birthDate;
        user.memberId = profile.memberId;
        user.knowledgeBase = profile.knowledgeBase;
        user.helpDeskQA = profile.helpDeskQA;
        user.accountStatus = profile.accountStatus;
      }
      return of(null);
    }
    validateUser(userName, memberId) {
      let response = new ValidateUserResponse();
      let user = this.findUser(x => x.userID == userName);
      if (user == null) response.status = "Available";else if (user.memberId == memberId) response.status = "AlreadyRegistered";else response.status = "Unavailable";
      return of(response);
    }
    findMember(memberId, birthDate) {
      let response = new FindMemberResponse();
      let user = this.findUser(x => x.memberId == memberId && x.birthDate == birthDate);
      if (user != null) {
        response.memberID = user.memberId;
        response.firstName = user.firstName;
        response.lastName = user.lastName;
        response.birthDate = user.birthDate;
      }
      return of(response);
    }
    findUsers(lastName, email, clientId, pageNumber, pageSize) {
      let response = [];
      let user = this.findUser(x => x.email == email);
      if (user != null) {
        response.push(user);
      }
      return of(response);
    }
    static {
      this.ɵfac = function MockAuthenticationService_Factory(t) {
        return new (t || MockAuthenticationService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MockAuthenticationService,
        factory: MockAuthenticationService.ɵfac
      });
    }
  }
  return MockAuthenticationService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AuthenticationModule = /*#__PURE__*/(() => {
  class AuthenticationModule {
    static {
      this.ɵfac = function AuthenticationModule_Factory(t) {
        return new (t || AuthenticationModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AuthenticationModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [AuthenticationService, MockAuthenticationService, AuthHelper, AuthGuard, AuthenticationConfig]
      });
    }
  }
  return AuthenticationModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of lib
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccountStatus, AuthGuard, AuthHelper, AuthProfile, AuthenticationConfig, AuthenticationFactor, AuthenticationModule, AuthenticationService, AuthenticationServiceResponse, AuthenticationServiceStatus, FindMemberRequest, FindMemberResponse, FindMemberUserRequest, FindUsersRequest, ForgotUsernameRequest, ForgotUsernameResponse, GetTokenRequest, GetTokenResponse, KnowledgeBase, LoginRequest, LoginResponse, MemberProfile, MemberProfileRequest, MockAuthenticationService, UserAddress, UserNameStatus, UserProfile, ValidateUserResponse };
